import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ownerApi, inr, monthLabel, errText } from "@/lib/ownerApi";

export const Stat = ({ label, value, cls, testid }) => (
  <div className={`rounded-2xl border p-4 ${cls}`} data-testid={testid}>
    <p className="text-xs font-semibold uppercase tracking-wide opacity-80">{label}</p>
    <p className="text-2xl font-extrabold mt-1">{inr(value)}</p>
  </div>
);

const Table = ({ head, rows, testid }) => (
  <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
    <table className="min-w-full text-sm" data-testid={testid}>
      <thead className="bg-stone-100 text-stone-600"><tr>{head.map((h) => <th key={h} className="px-3 py-2 text-left font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
      <tbody className="divide-y divide-stone-100">{rows}</tbody>
    </table>
  </div>
);

export default function Overview() {
  const [d, setD] = useState(null);
  useEffect(() => { ownerApi.get("/overview").then(({ data }) => setD(data)).catch((e) => toast.error(errText(e))); }, []);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-emerald-800 mx-auto" />;
  const pct = d.totals.due ? Math.round((d.totals.earned / d.totals.due) * 100) : 0;
  return (
    <div className="space-y-6" data-testid="owner-overview">
      <div>
        <h2 className="font-heading text-xl font-bold text-stone-900">Academic year {d.ay}</h2>
        <p className="text-sm text-stone-500">Due = full-year fees on fee plans (live). Earned = approved payments. {pct}% collected.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Income due" value={d.totals.due} cls="bg-white border-stone-200 text-stone-900" testid="owner-total-due" />
        <Stat label="Income earned" value={d.totals.earned} cls="bg-emerald-50 border-emerald-200 text-emerald-900" testid="owner-total-earned" />
        <Stat label="Pending" value={d.totals.pending} cls="bg-amber-50 border-amber-200 text-amber-900" testid="owner-total-pending" />
      </div>
      <section className="space-y-2">
        <h3 className="font-bold text-stone-900">By month</h3>
        <Table testid="owner-months-table" head={["Month", "Due", "Earned", "Difference"]} rows={d.months.map((m) => (
          <tr key={m.month} data-testid={`owner-month-${m.month}`}><td className="px-3 py-2">{monthLabel(m.month)}</td><td className="px-3 py-2">{inr(m.due)}</td>
            <td className="px-3 py-2 text-emerald-800 font-semibold">{inr(m.earned)}</td><td className="px-3 py-2">{inr(m.earned - m.due)}</td></tr>))} />
        <p className="text-xs text-stone-500">Lump-sum fees count as due in their due-date month. Earned is by approval date.</p>
      </section>
      <section className="space-y-2">
        <h3 className="font-bold text-stone-900">By salary group</h3>
        <Table testid="owner-groups-table" head={["Group", "Students", "Due", "Earned", "Pending"]} rows={d.groups.map((g) => (
          <tr key={g.group} data-testid={`owner-group-${g.group}`}><td className="px-3 py-2 font-semibold">{g.group}</td><td className="px-3 py-2">{g.students}</td>
            <td className="px-3 py-2">{inr(g.due)}</td><td className="px-3 py-2 text-emerald-800">{inr(g.earned)}</td><td className="px-3 py-2 text-amber-800">{inr(g.pending)}</td></tr>))} />
      </section>
    </div>
  );
}
